import React, { useState, useRef } from 'react';
import {
  Modal,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks/useAppDispatch';

import styles from './CreateCustomerContactModal.module.scss';
import { NWClient } from '../../client/NWClient';
import { fetchCustomerById } from '../../slices/customers/customersSlice';
import { Customer } from '../../models/customer.model';
import { toast } from 'react-toastify';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

interface CreateCustomerContactModalProps {
  open: boolean;
  onClose: () => void;
  customer: Customer;
  companyId: number;
  onSuccessfulSubmit?: () => void;
}

export default function CreateCustomerContactModal({
  open,
  onClose,
  onSuccessfulSubmit,
  customer,
}: CreateCustomerContactModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLDivElement | null>(null);

  const [formData, setFormData] = useState({
    detail_type: '',
    detail: '',
    customer_id: customer.id,
  });

  const handleChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCreate = async () => {
    dispatch(showOverlay());

    try {
      // Check if the detail_type already exists
      const isDuplicate = customer.contact_details.some(
        (contact) => contact.detail_type === formData.detail_type
      );

      if (isDuplicate) {
        toast.error('This contact type already exists for the customer.');
        dispatch(hideOverlay());
        return;
      }

      // Proceed with API call if no duplicate
      NWClient.post('customer-contact-details', formData, true).then(() => {
        toast.success(t('messages.preferredContactMethodAdded'));
        dispatch(fetchCustomerById(formData.customer_id));
        dispatch(hideOverlay());
      });
      onSuccessfulSubmit();
    } catch (error) {
      toast.error(error.message ? error.message : t('messages.errorOccurred'));
    } finally {
      dispatch(hideOverlay());
    }
  };

  const contactMethodOptions = [
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'email_address', label: 'Email Address' },
    { key: 'social_media', label: 'Social Media' },
  ];

  return (
    <Modal open={open} onClose={onClose} className={'action-modal'} closeAfterTransition>
      <Grid container className={styles.modal_container}>
        <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Card ref={formRef}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant='h6' gutterBottom>
                {t('labels.preferredContactMethod')}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Select
                    fullWidth
                    value={formData.detail_type ?? ''}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleChange('detail_type')(e.target.value);
                    }}
                    displayEmpty
                    inputProps={{ 'aria-label': t('labels.preferredContactMethod') }}
                  >
                    <MenuItem value='' disabled>
                      Select contact method
                    </MenuItem>
                    {contactMethodOptions.map((option) => (
                      <MenuItem
                        key={option.key}
                        value={option.key}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {formData.detail_type !== '' && (
                    <TextField
                      fullWidth
                      className={styles.textfield_animation}
                      type='text'
                      label={
                        contactMethodOptions.find((option) => option.key === formData.detail_type)
                          ?.label || ''
                      }
                      value={formData.detail}
                      onChange={(e) => handleChange('detail')(e.target.value)}
                      variant='outlined'
                    />
                  )}
                </Grid>
              </Grid>
              <Stack direction='row' gap={'8px'} justifyContent='flex-end' marginTop={'16px'}>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onClose}
                >
                  {t('buttons.close')}
                </Button>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={handleCreate}
                >
                  {t('buttons.create')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
}
